import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { SwitchField } from "components/ui/switch";
import SportCategoryChip from "components/SportCategoryChip";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import { EVENT_CATEGORIES, EVENT_STATUSES, categoryLabel } from "lib/events";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2";

const EMPTY = {
  name: "",
  description: "",
  championship: "",
  start_date: "",
  end_date: "",
  category: "sports",
  sport: "",
  sport_category: "",
  location_name: "",
  address: "",
  city: "",
  state: "",
  country: "",
  status: "draft",
  watermark: false,
};

// So os campos do formulario: a resposta traz contadores, slug e capa, que nao
// sao editaveis aqui (e nao podem vazar para o payload de criacao).
const formFromEvent = (data) =>
  Object.keys(EMPTY).reduce(
    // `championship`, `sport` e `sport_category` vem null quando vazios; o
    // <select> precisa de string vazia para casar com a opcao "nenhum".
    (acc, field) => ({ ...acc, [field]: data[field] ?? EMPTY[field] }),
    {}
  );

export default function EventForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, tf } = useI18n();
  const isEditing = !!id;

  // Duplicar e so front-end: carrega o evento de origem, preenche o formulario
  // e nada e persistido enquanto o usuario nao salvar.
  const duplicateFrom = !isEditing ? location.state?.duplicateFrom : null;
  const duplicateName = location.state?.duplicateName || "";
  const isDuplicating = !!duplicateFrom;

  const [form, setForm] = useState(EMPTY);
  const [championships, setChampionships] = useState([]);
  const [sports, setSports] = useState([]);
  const [sportCategories, setSportCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing || isDuplicating);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  useEffect(() => {
    const sourceId = id || duplicateFrom;
    if (!sourceId) return;
    api
      .get(`/api/events/${sourceId}/`)
      .then((res) => {
        const data = formFromEvent(res.data);
        // Copia so a configuracao: nome, capa e fotos ficam de fora.
        setForm(duplicateFrom ? { ...data, name: "" } : data);
      })
      .catch(() => setError(t("common.loadError")))
      .finally(() => setFetching(false));
  }, [id, duplicateFrom, t]);

  // Campeonatos para o seletor — o vinculo e opcional (evento avulso existe).
  useEffect(() => {
    api
      .get("/api/championships/")
      .then((res) => setChampionships(res.data || []))
      .catch(() => setChampionships([]));
  }, []);

  // Modalidades cadastradas (menu Modalidades) — nao ha mais lista fixa.
  useEffect(() => {
    api
      .get("/api/sports/")
      .then((res) => setSports(res.data || []))
      .catch(() => setSports([]));
  }, []);

  // Categorias SO da modalidade escolhida.
  const sportId = form.category === "sports" ? form.sport : "";
  useEffect(() => {
    if (!sportId) {
      setSportCategories([]);
      return;
    }
    api
      .get("/api/sport-categories/", { params: { sport: sportId } })
      .then((res) => setSportCategories(res.data || []))
      .catch(() => setSportCategories([]));
  }, [sportId]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Trocar a modalidade solta a categoria: ela pertence a modalidade anterior.
  const setSport = (e) =>
    setForm((prev) => ({ ...prev, sport: e.target.value, sport_category: "" }));

  const selectedCategory = sportCategories.find(
    (item) => item.id === form.sport_category
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Modalidade e categoria so acompanham evento esportivo — o backend
    // valida de novo. "" no <select> significa nenhum: o backend espera null.
    const isSports = form.category === "sports";
    const payload = {
      ...form,
      sport: isSports ? form.sport || null : null,
      sport_category: isSports ? form.sport_category || null : null,
      // "" no <select> significa evento avulso: o backend espera null.
      championship: form.championship || null,
    };

    try {
      if (isEditing) {
        await api.put(`/api/events/${id}/`, payload);
        navigate(`/app/events/${id}`);
      } else {
        const res = await api.post("/api/events/", payload);
        navigate(`/app/events/${res.data.id}`);
      }
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === "object"
          ? Object.values(detail).flat().join(" ")
          : t("common.saveError")
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/events")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isEditing
                  ? t("event.edit")
                  : isDuplicating
                  ? t("event.duplicateTitle")
                  : t("event.new")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditing
                  ? t("event.editSubtitle")
                  : isDuplicating
                  ? tf("event.duplicateSubtitle", { name: duplicateName })
                  : t("event.newSubtitle")}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="name">{t("event.name")}</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={set("name")}
                  required
                  maxLength={200}
                  placeholder={t("event.namePlaceholder")}
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="championship">{t("event.championship")}</Label>
                <select
                  id="championship"
                  value={form.championship || ""}
                  onChange={set("championship")}
                  className={SELECT_CLASS}
                >
                  {/* Vinculo opcional: formatura, casamento e afins nao
                      pertencem a competicao nenhuma. */}
                  <option value="">{t("event.noChampionship")}</option>
                  {championships.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.season ? `${item.name} — ${item.season}` : item.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("event.championshipHint")}
                </p>
              </div>

              <div>
                <Label htmlFor="start_date">{t("event.startDate")}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date || ""}
                  onChange={set("start_date")}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_date">{t("event.endDate")}</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date || ""}
                  onChange={set("end_date")}
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">{t("event.category")}</Label>
                <select
                  id="category"
                  value={form.category}
                  onChange={set("category")}
                  className={SELECT_CLASS}
                >
                  {EVENT_CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {categoryLabel(t, value)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Modalidade e categoria so existem em evento esportivo. */}
              {form.category === "sports" && (
                <div>
                  <Label htmlFor="sport">{t("event.sport")}</Label>
                  <select
                    id="sport"
                    value={form.sport || ""}
                    onChange={setSport}
                    required
                    className={SELECT_CLASS}
                  >
                    <option value="" disabled>
                      {t("event.sportPlaceholder")}
                    </option>
                    {sports.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {form.category === "sports" && (
                <div className="md:col-span-2">
                  <Label htmlFor="sport_category">{t("event.sportCategory")}</Label>
                  <div className="flex items-center gap-3">
                    <select
                      id="sport_category"
                      value={form.sport_category || ""}
                      onChange={set("sport_category")}
                      disabled={!form.sport}
                      className={SELECT_CLASS}
                    >
                      <option value="">{t("event.noSportCategory")}</option>
                      {sportCategories.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {/* Previa: exatamente o chip que o card publico exibe. */}
                    <SportCategoryChip
                      chip={selectedCategory}
                      className="shrink-0"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("event.sportCategoryHint")}
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <Label htmlFor="description">{t("event.description")}</Label>
                <textarea
                  id="description"
                  value={form.description || ""}
                  onChange={set("description")}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-muted-foreground">
                {t("event.locationSection")}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="location_name">{t("event.locationName")}</Label>
                  <Input
                    id="location_name"
                    value={form.location_name || ""}
                    onChange={set("location_name")}
                    placeholder={t("event.locationPlaceholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="address">{t("event.address")}</Label>
                  <Input
                    id="address"
                    value={form.address || ""}
                    onChange={set("address")}
                  />
                </div>
                <div>
                  <Label htmlFor="city">{t("common.city")}</Label>
                  <Input id="city" value={form.city || ""} onChange={set("city")} />
                </div>
                <div>
                  <Label htmlFor="state">{t("common.state")}</Label>
                  <Input
                    id="state"
                    value={form.state || ""}
                    onChange={set("state")}
                  />
                </div>
                <div>
                  <Label htmlFor="country">{t("common.country")}</Label>
                  <Input
                    id="country"
                    value={form.country || ""}
                    onChange={set("country")}
                    maxLength={2}
                    placeholder={t("common.countryPlaceholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="status">{t("event.status")}</Label>
                  <select
                    id="status"
                    value={form.status}
                    onChange={set("status")}
                    className={SELECT_CLASS}
                  >
                    {EVENT_STATUSES.map((value) => (
                      <option key={value} value={value}>
                        {t(`event.status.${value}`)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("event.statusHint")}
                  </p>
                </div>
              </div>
            </div>

            <SwitchField
              checked={!!form.watermark}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, watermark: value }))
              }
              label={t("event.watermark")}
              description={t("event.watermarkHint")}
            />

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/app/events")}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={loading}>
                {isEditing ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
